import React, { Fragment, type SetStateAction, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInfiniteQuery, useMutation } from "react-query"
import { useSelector } from "react-redux"
import { Link, useParams } from "react-router-dom"

import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined"
import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, CardContent, CardHeader, Grid, IconButton } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import {
  Button,
  InputText,
  Menu,
  MenuItem,
  NotificationUtils,
  Skeleton,
  Tooltip,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FieldArray, useFormikContext } from "formik"
import { v4 as uuidv4 } from "uuid"

import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { LabelsMenu } from "../../../components/form/LabelsMenu"
import { getTheme } from "../../../hooks/UseTheme"
import { useDebounce } from "../../../hooks/useDebounce"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { RootState } from "../../../store/ReduxStore"
import { type TextColor } from "../../../types/custom/projects"
import type { Label } from "../../../types/generated/api/Label"
import type { PaginatedLabelList } from "../../../types/generated/api/PaginatedLabelList"
import { extractPageFromBackEndPaginationLink, getEnvVar } from "../../../utils/genericHelpers"
import type { TagCardFormikValues } from "../../Taglist"
import { createEmptyCondition, handleAddNewValue, handleRemoveCondition } from "../helpers"
import type { DataBlockProps, RuleCardFormikValues, RuleCardProps, RuleGroupConditions } from "../interfaces"
import { Condition } from "./Condition"
import { GroupRenderer } from "./GroupRenderer"

import styles from "./RuleCard.module.scss"

/**
 * Helper UI component to render data items in ruleset card
 * @param {string} value
 * @return {React.ReactElement}
 */
export function DataBlock(props: Readonly<DataBlockProps>): React.ReactElement {
  const {
    value,
    isLoading,
    textVariant = "h3-bold",
    bgVariant = "gray",
    variantColor = 1,
    textColor = "base",
    fileId,
  } = props
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  if (fileId) {
    return (
      <div className={bgVariant === "gray" ? styles.cardFieldLight : styles.cardFieldDark}>
        <Tooltip title={value}>
          <Link
            to={`/projects/${currentProject?.uuid}/data-files?page=Data-files&file_id=${fileId}`}
            className={styles.fileName}
          >
            <Typography variant={textVariant} color={"important"} variantColor={2} noWrap className={styles.fileName}>
              {isLoading ? <Skeleton variant="rectangular" width={"100%"} height={20} /> : value}
            </Typography>
          </Link>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className={bgVariant === "gray" ? styles.cardFieldLight : styles.cardFieldDark}>
      {typeof value === "string" ? (
        <Tooltip title={value}>
          <Typography variant={textVariant} color={textColor as TextColor} variantColor={variantColor} noWrap>
            {isLoading ? <Skeleton variant="rectangular" width={"100%"} height={20} /> : value}
          </Typography>
        </Tooltip>
      ) : (
        value
      )}
    </div>
  )
}

/**
 * Rule card component
 * @return {React.ReactElement}
 */
export function RuleCard(props: Readonly<RuleCardProps>): React.ReactElement {
  const { editMode = false, createMode = false, handleDelete, handleRemoveRule, index, andOr } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { values, setFieldValue, handleChange, handleBlur, setValues, isSubmitting, errors, touched } =
    useFormikContext<RuleCardFormikValues>()

  const theme = getTheme()

  const { t } = useTranslation("rulesets")

  const [openRuleDeletionDialog, setOpenRuleDeletionDialog] = useState<boolean>(false)

  const emptyCondition = createEmptyCondition()
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null)
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(menuAnchor)

  const openRuleCardMenu = Boolean(anchor)

  const pageSize = 20
  const [page, setPage] = useState<number>(0)
  const [searchKey, setSearchKey] = useState<string | null | undefined>(values.cards[index]?.label)

  const [labels, setLabels] = useState<Label[]>([])

  const MAX_GROUP_DEPTH = Number(getEnvVar("RULESETS_TAGLISTS_GROUPS_DEPTH", 3))

  const CreateLabelMutation = useMutation<
    AxiosResponse<Label>,
    AxiosError,
    {
      project_uuid: string
      name: string
    }
  >(KonanAPI.CreateLabel, {
    onSuccess: async ({ data }) => {
      NotificationUtils.toast(t("notifications.labelCreated"), { severity: "success" })

      setFieldValue(`cards[${index}].label`, data.name)
      setFieldValue(`cards[${index}].label_id`, data.uuid)

      // Add the new label to the labels array
      setLabels((prevLabels) => [...prevLabels, data])

      await queryClient.invalidateQueries("label-list")
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.labelCreated"), { severity: "error" })
    },
  })

  // fetch project labels
  const {
    isLoading: isLabelListLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedLabelList>, AxiosError, Label>(
    ["label-list", projectId, page, pageSize, searchKey],
    ({ pageParam = 1 }) =>
      KonanAPI.RetrieveLabels({
        project_uuid: projectId as string,
        pageSize: pageSize,
        page: pageParam,
        search: searchKey,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<Label[]>((accumulator, page) => accumulator.concat(page.data.results as Label[]), []),
      }),
      onSuccess: (response) => {
        // filtering out already existing labels in the list
        const tempLabels =
          response.pages?.filter((item: Label) => !labels.find((label: Label) => label.uuid === item.uuid)) ?? []

        setLabels([...labels, ...tempLabels])

        !["", null, undefined].includes(searchKey) &&
          // setting name (automatically) ONLY if the user writes the full name correctly
          response.pages[0]?.name === searchKey &&
          setFieldValue(`cards[${index}].label_id`, response.pages[0]?.uuid)
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: editMode || createMode,
      refetchOnMount: true,
    },
  )

  const debouncedOnChange = useDebounce((): void => {
    const label = values.cards[index]?.label

    setPage(0)
    setSearchKey(label === "" ? null : label)
  }, 400)

  return (
    <Card className={styles.card}>
      {openRuleDeletionDialog && (
        <BaseSimpleDialog
          open={openRuleDeletionDialog}
          name={values.cards[index]?.name ?? ""}
          onClose={() => setOpenRuleDeletionDialog(false)}
          onAccept={() => {
            if (handleDelete) {
              handleDelete(values?.cards[index]?.name ?? "", setOpenRuleDeletionDialog)
            }
          }}
          mode={"rule-deletion"}
          isLoading={isSubmitting}
        />
      )}

      <CardHeader
        className={styles.cardHeader}
        title={
          <Grid container justifyContent="flex-start" alignItems="center" spacing={2}>
            <Grid item xs={6}>
              {editMode || createMode ? (
                <InputText
                  id={`cards[${index}].name`}
                  hideDescription
                  placeholder={t("inputNamePlaceHolder")}
                  value={values.cards[index]?.name}
                  handleChange={handleChange}
                  error={
                    touched?.cards &&
                    errors?.cards &&
                    touched?.cards[index]?.name &&
                    Boolean((errors?.cards[index] as RuleGroupConditions)?.name) &&
                    (errors?.cards[index] as RuleGroupConditions)?.name
                  }
                  handleBlur={handleBlur}
                  disabled={isSubmitting}
                  description={t("ruleDescription")}
                  fullWidth
                />
              ) : (
                <Typography variant="h3-bold" style={{ textTransform: "inherit" }}>
                  {values.cards[index]?.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        }
        action={
          handleDelete &&
          handleRemoveRule && (
            <Fragment>
              <IconButton
                aria-label="settings"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}
              >
                <MoreHorizIcon htmlColor={theme.palette.base.text[1]} />
              </IconButton>
              <Menu
                key="basic-menu"
                anchorEl={anchor}
                open={openRuleCardMenu}
                onClose={() => setAnchor(null)}
                menuMaxContent
              >
                <Tooltip width={"100%"} title={1 === values?.cards?.length && t("rulesetMinRuleTooltip")}>
                  <MenuItem
                    onClick={() => {
                      const removeIndicator = handleRemoveRule?.(index)
                      if (removeIndicator) {
                        setOpenRuleDeletionDialog(true)
                      }
                      setAnchor(null)
                    }}
                    disabled={values?.cards?.length === 1 || isSubmitting}
                  >
                    <Typography variant="a" color="negative" variantColor={2}>
                      {t("removeRuleBtn")}
                    </Typography>
                  </MenuItem>
                </Tooltip>
              </Menu>
            </Fragment>
          )
        }
      />

      <CardContent className={styles.cardContent}>
        <FieldArray name={`cards[${index}].conditions`}>
          {/* Level 1 group */}
          {({ push: level1push, remove: level1remove }) => (
            <Grid container justifyContent="flex-end" alignItems="center" className={styles.groupBackground}>
              <Grid container item spacing={1}>
                {values?.cards[index]?.conditions?.map((item, idx: number) => (
                  <Grid item xs={12} key={`level1-${index}-${idx}-${item.id}`}>
                    <Condition
                      key={item?.id}
                      path={{ groupsIdx: [], conditionIdx: idx }}
                      id={`cards[${index}].conditions[${idx}]`}
                      index={idx}
                      editMode={editMode}
                      createMode={createMode}
                      ruleIndex={index}
                      removeCondition={() => {
                        handleRemoveCondition({
                          values,
                          setFieldValue,
                          setValues: setValues as (
                            values: SetStateAction<RuleCardFormikValues | TagCardFormikValues>,
                          ) => void,
                          levelRemove: level1remove,
                          ruleIndex: index,
                          path: {
                            groupsIdx: [],
                            conditionIdx: idx,
                          },
                        })
                        level1remove(idx)
                      }}
                      lastSwitchSetter={(op) => {
                        setFieldValue(`cards[${index}].conditions[0].andOr`, op)
                      }}
                    />
                  </Grid>
                ))}

                {/* Recursive rendering of nested groups */}
                {values.cards[index]?.groups && values.cards[index]?.groups.length > 0 && (
                  <GroupRenderer
                    groups={values.cards[index].groups}
                    ruleIndex={index}
                    parentPath={[]}
                    editMode={editMode}
                    createMode={createMode}
                    currentDepth={0}
                    maxDepth={MAX_GROUP_DEPTH}
                    andOr={andOr}
                    setValues={setValues}
                  />
                )}

                {/* level 1 menu button  */}
                {(editMode || createMode) && (
                  <Grid container justifyContent="flex-start" alignItems="center">
                    <Grid item lg={12} mt={1} ml={1}>
                      <Button
                        size="regular"
                        onClick={(e) => setMenuAnchor(e?.currentTarget as HTMLElement)}
                        disabled={isSubmitting}
                      >
                        + {t("addRuleBtn")}
                      </Button>
                      <Menu
                        key="basic-menu1"
                        anchorEl={menuAnchor}
                        open={isMenuOpen}
                        onClose={() => setMenuAnchor(null)}
                        menuMaxContent
                      >
                        <MenuItem
                          onClick={() => {
                            level1push({ ...emptyCondition, andOr: "and" })
                            setMenuAnchor(null)
                          }}
                        >
                          <Grid container display={"flex"} gap={1} height={"20px"}>
                            <Grid item>
                              <AddOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                            </Grid>
                            <Grid item>
                              <Typography variant="a">{t("addRuleBtn")}</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            const newGroup = {
                              id: uuidv4(),
                              andOr: "and",
                              conditions: [emptyCondition as never, emptyCondition as never],
                              groups: [],
                            }
                            setFieldValue(`cards[${index}].groups`, [...(values.cards[index]?.groups || []), newGroup])
                            setMenuAnchor(null)
                          }}
                        >
                          <Grid container display={"flex"} gap={1} height={"20px"}>
                            <Grid item>
                              <AddBoxOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                            </Grid>
                            <Grid item>
                              <Typography variant="a">{t("addGroupBtn")}</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>
                      </Menu>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </FieldArray>

        <Grid container justifyContent="flex-start" alignItems="center" spacing={2} mt={2}>
          {/* Return */}
          <Grid item xs={3} lg={1} display="flex" justifyContent="flex-end">
            <Typography variant="p">{t("return")}</Typography>
          </Grid>

          {/* Label */}
          <Grid item xs={9} lg={11}>
            {editMode || createMode ? (
              <LabelsMenu
                filteredLabels={labels}
                value={values.cards[index]?.label ?? ""}
                onChange={(e: InputChangeEvent) => {
                  handleChange(e)
                  setFieldValue(`cards[${index}].label`, e.target.value)
                  setFieldValue(`cards[${index}].label_id`, "")
                  debouncedOnChange()
                }}
                onSelectingLabel={(item) => {
                  setFieldValue(`cards[${index}].label_id`, item.uuid)
                  setFieldValue(`cards[${index}].label`, item.name)
                }}
                onAddingNewLabel={async () => {
                  await handleAddNewValue({
                    values,
                    setFieldValue,
                    index,
                    projectId: projectId as string,
                    createLabelMutation: CreateLabelMutation,
                  })
                }}
                disabled={isSubmitting}
                placeholder={t("labelPlaceholder")}
                onLoadMore={() => fetchNextPage()}
                hasMore={hasNextPage}
                isLoading={isLabelListLoading || isFetchingNextPage}
                error={
                  touched?.cards &&
                  errors?.cards &&
                  touched?.cards[index]?.label &&
                  Boolean((errors?.cards[index] as RuleGroupConditions)?.label_id) &&
                  (errors?.cards[index] as RuleGroupConditions)?.label_id
                }
              />
            ) : (
              <DataBlock value={values.cards[index]?.label ?? values.cards[index]?.label} />
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
